
import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Upload, Shield, CheckCircle, Clock, XCircle, AlertTriangle, FileImage, Camera, MessageSquare } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface KYCSectionProps {
  onUploadClick: () => void;
}

const KYCSection = ({ onUploadClick }: KYCSectionProps) => {
  const { profile } = useAuth();
  const { t } = useLanguage();
  const [hasDocuments, setHasDocuments] = useState<boolean>(false);

  // Check if user has uploaded documents
  useEffect(() => {
    if (profile?.id_front_url || profile?.id_back_url) {
      setHasDocuments(true);
    } else {
      setHasDocuments(false);
    }
  }, [profile?.id_front_url, profile?.id_back_url]);

  // Set up real-time listener with error handling
  useEffect(() => {
    if (!profile?.id) return;

    console.log('🔄 Setting up KYC updates for user:', profile.id);
    
    let channel: any = null;
    
    try {
      console.log('🔗 Setting up KYC real-time updates');
      
      channel = supabase
        .channel(`kyc-status-${profile.id}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'profiles',
            filter: `id=eq.${profile.id}`
          },
          (payload) => {
            console.log('📡 KYC status update received:', payload);
          }
        )
        .subscribe((status) => {
          console.log('KYC subscription status:', status);
        });
    } catch (error) {
      console.error('❌ Real-time setup failed:', error);
    }

    return () => {
      console.log('Cleaning up KYC subscription');
      if (channel) {
        try {
          supabase.removeChannel(channel);
        } catch (error) {
          console.log('Error cleaning up channel:', error);
        }
      }
    };
  }, [profile?.id]);

  const getKYCStatusBadge = (status?: string) => {
    switch (status) {
      case 'VERIFIED':
        return (
          <Badge variant="default" className="bg-green-500">
            <CheckCircle className="h-3 w-3 mr-1" />
            {t('verified', 'مُتحقق', 'Verified')}
          </Badge>
        );
      case 'PENDING':
        return (
          <Badge variant="secondary" className="bg-blue-500 text-white">
            <Clock className="h-3 w-3 mr-1" />
            {t('pending', 'في الانتظار', 'Pending')}
          </Badge>
        );
      case 'UNDER_REVIEW':
        return (
          <Badge variant="outline" className="border-orange-500 text-orange-700">
            <Clock className="h-3 w-3 mr-1" />
            {t('under_review', 'قيد المراجعة', 'Under Review')}
          </Badge>
        );
      case 'REJECTED':
        return (
          <Badge variant="destructive">
            <XCircle className="h-3 w-3 mr-1" />
            {t('rejected', 'مرفوض', 'Rejected')}
          </Badge>
        );
      default:
        return (
          <Badge variant="secondary">
            <AlertTriangle className="h-3 w-3 mr-1" />
            {t('not_started', 'لم يبدأ', 'Not Started')}
          </Badge>
        );
    }
  };

  const getStatusMessage = (status?: string) => {
    switch (status) {
      case 'VERIFIED':
        return t('kyc_verified_message', 'تم التحقق من هويتك بنجاح! يمكنك الآن الوصول إلى جميع الميزات.', 'Your identity has been verified successfully! You can now access all features.');
      case 'PENDING':
        return t('kyc_pending_message', 'المستندات قيد المراجعة من قبل الإدارة. سيتم إشعارك بالنتيجة قريبًا.', 'Documents are under review by administration. You will be notified of the result soon.');
      case 'UNDER_REVIEW':
        return t('kyc_review_message', 'يتم مراجعة مستنداتك حاليًا من قبل فريق التحقق.', 'Your documents are currently being reviewed by our verification team.');
      case 'REJECTED':
        return t('kyc_rejected_message', 'تم رفض المستندات. يرجى مراجعة المتطلبات ورفع مستندات جديدة.', 'Documents were rejected. Please review the requirements and upload new documents.');
      default:
        return t('kyc_not_started_message', 'يجب رفع مستندات الهوية الوطنية لإكمال التحقق من هويتك.', 'You need to upload your National ID documents to complete identity verification.');
    }
  };

  const getAlertVariant = (status?: string) => {
    switch (status) {
      case 'VERIFIED':
        return 'default';
      case 'REJECTED':
        return 'destructive';
      default:
        return 'default';
    }
  };

  const shouldShowUploadButton = () => {
    return !profile?.kyc_status || 
           profile?.kyc_status === 'NOT_STARTED' || 
           profile?.kyc_status === 'REJECTED' ||
           profile?.kyc_status === 'PENDING';
  };

  const getUploadButtonText = () => {
    if (!hasDocuments) {
      return t('upload_id_documents', 'رفع وثائق الهوية', 'Upload ID Documents');
    }
    if (profile?.kyc_status === 'REJECTED') {
      return t('reupload_documents', 'إعادة رفع المستندات', 'Re-upload Documents');
    }
    return t('manage_documents', 'إدارة المستندات', 'Manage Documents');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shield className="h-5 w-5 text-primary" />
            {t('kyc_verification', 'التحقق من الهوية', 'Identity Verification')}
          </div>
          {getKYCStatusBadge(profile?.kyc_status)}
        </CardTitle>
        <CardDescription>
          {t('kyc_description', 'رفع مستندات الهوية الوطنية للتحقق من صحة البيانات والوصول للميزات المتقدمة', 'Upload National ID documents for identity verification and access to advanced features')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Contact Feature Alert */}
        {(!profile?.id_front_url || !profile?.id_back_url) && (
          <Alert className="border-blue-200 bg-blue-50 dark:bg-blue-950 dark:border-blue-800">
            <MessageSquare className="h-4 w-4 text-blue-600 dark:text-blue-400" />
            <AlertDescription className="text-blue-800 dark:text-blue-200">
              <div className="font-medium mb-1">
                {t('kyc_required_for_contact', 'مطلوب للتواصل مع المالكين', 'Required to Contact Property Owners')}
              </div>
              <p className="text-sm">
                {t('kyc_contact_explanation', 
                  'رفع صور الهوية الشخصية مطلوب للتواصل مع المالكين عبر واتساب لضمان الأمان والثقة بين المستخدمين.',
                  'Uploading your ID documents is required to contact property owners via WhatsApp to ensure safety and trust between users.'
                )}
              </p>
            </AlertDescription>
          </Alert>
        )}

        {/* Status Alert */}
        <Alert variant={getAlertVariant(profile?.kyc_status)}>
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription className="font-medium">
            {getStatusMessage(profile?.kyc_status)}
          </AlertDescription>
        </Alert>

        {/* Requirements Alert - Show when not verified */}
        {profile?.kyc_status !== 'VERIFIED' && (
          <Alert>
            <Camera className="h-4 w-4" />
            <AlertDescription>
              <div className="space-y-2">
                <div className="font-medium">
                  {t('document_requirements', 'متطلبات المستندات:', 'Document Requirements:')}
                </div>
                <ul className="list-disc list-inside text-sm space-y-1 ml-4">
                  <li>{t('req_front_id', 'صورة واضحة للوجه الأمامي لبطاقة الهوية الوطنية', 'Clear photo of National ID front side')}</li>
                  <li>{t('req_back_id', 'صورة واضحة للوجه الخلفي لبطاقة الهوية الوطنية', 'Clear photo of National ID back side')}</li>
                  <li>{t('req_file_format', 'صيغة الملف: JPG، PNG، أو PDF (حد أقصى 5MB)', 'File format: JPG, PNG, or PDF (max 5MB)')}</li>
                  <li>{t('req_visibility', 'يجب أن تكون جميع المعلومات واضحة وقابلة للقراءة', 'All information must be clear and readable')}</li>
                </ul>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* Document Status */}
        {hasDocuments && (
          <div className="grid grid-cols-2 gap-4">
            <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/20">
              <FileImage className="h-4 w-4 text-green-600" />
              <div className="flex-1">
                <p className="text-sm font-medium">
                  {t('front_id', 'الهوية الأمامية', 'Front ID')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {profile?.id_front_url ? t('uploaded', 'مرفوعة', 'Uploaded') : t('missing', 'مفقودة', 'Missing')}
                </p>
              </div>
              {profile?.id_front_url ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <XCircle className="h-4 w-4 text-red-500" />
              )}
            </div>
            
            <div className="flex items-center gap-2 p-3 border rounded-lg bg-muted/20">
              <FileImage className="h-4 w-4 text-green-600" />
              <div className="flex-1">
                <p className="text-sm font-medium">
                  {t('back_id', 'الهوية الخلفية', 'Back ID')}
                </p>
                <p className="text-xs text-muted-foreground">
                  {profile?.id_back_url ? t('uploaded', 'مرفوعة', 'Uploaded') : t('missing', 'مفقودة', 'Missing')}
                </p>
              </div>
              {profile?.id_back_url ? (
                <CheckCircle className="h-4 w-4 text-green-600" />
              ) : (
                <XCircle className="h-4 w-4 text-red-500" />
              )}
            </div>
          </div>
        )}
        
        {/* Upload Button */}
        {shouldShowUploadButton() && (
          <Button 
            onClick={onUploadClick} 
            className="w-full"
            size="lg"
            variant={profile?.kyc_status === 'REJECTED' ? 'destructive' : 'default'}
          >
            <Upload className="h-4 w-4 mr-2" />
            {getUploadButtonText()}
          </Button>
        )}

        {/* Processing Timeline */}
        {profile?.kyc_status === 'PENDING' && (
          <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200 mb-2">
              <Clock className="h-4 w-4" />
              <span className="font-medium">
                {t('processing_timeline', 'المدة الزمنية للمراجعة', 'Processing Timeline')}
              </span>
            </div>
            <p className="text-sm text-blue-700 dark:text-blue-300">
              {t('kyc_processing_time', 'عادة ما تستغرق مراجعة المستندات 24-48 ساعة عمل. سيتم إشعارك فور انتهاء المراجعة.', 'Document review usually takes 24-48 business hours. You will be notified as soon as the review is complete.')}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default KYCSection;
